import { useEffect, useMemo, useState } from "react";

import { motion } from "framer-motion";

import { toast } from "sonner";

import {
  Bank,
  Calculator,
  Plus,
  Receipt,
  Trash,
  X,
  PencilSimple,
} from "@phosphor-icons/react";

import api from "../lib/api";

export default function Contabilidad() {
  const [tab, setTab] = useState("asientos");
  const [asientos, setAsientos] = useState([]);
  const [balance, setBalance] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cuentaModal, setCuentaModal] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [cuentaForm, setCuentaForm] = useState({
    codigo: "",
    nombre: "",
    tipo: "gasto",
  });
  const [editingAsiento, setEditingAsiento] = useState(null);

  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    cuenta_id: "",
    lineas: [
      {
        concepto: "",
        debe: "",
        haber: "",
      },
    ],
  });

  const tipoColor = (tipo) => {
    switch (tipo) {
      case "activo":
        return "bg-blue-100 text-blue-700";
      case "pasivo":
        return "bg-purple-100 text-purple-700";
      case "ingreso":
        return "bg-green-100 text-green-700";
      case "gasto":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const load = async () => {
    try {
      setLoading(true);

      const [asientosRes, balanceRes, cuentasRes] = await Promise.all([
        api.get("/contabilidad/asientos"),
        api.get("/contabilidad/balance"),
        api.get("/contabilidad/cuentas"),
      ]);

      setAsientos(asientosRes.data);
      setBalance(balanceRes.data);
      setCuentas(cuentasRes.data);
    } catch {
      toast.error("No se pudo cargar la contabilidad");
    } finally {
      setLoading(false);
    }
  };

  const updateLinea = (index, field, value) => {
    setForm((prev) => {
      const lineas = [...prev.lineas];
      lineas[index] = {
        ...lineas[index],
        [field]: value,
      };
      return {
        ...prev,
        lineas,
      };
    });
  };

  const addLinea = () => {
    setForm((prev) => ({
      ...prev,
      lineas: [
        ...prev.lineas,
        {
          concepto: "",
          debe: "",
          haber: "",
        },
      ],
    }));
  };

  const removeLinea = (index) => {
    setForm((prev) => ({
      ...prev,
      lineas: prev.lineas.filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    load();
  }, []);

  const totalDebe = useMemo(
    () => balance.reduce((acc, b) => acc + Number(b.debe), 0),
    [balance],
  );

  const totalHaber = useMemo(
    () => balance.reduce((acc, b) => acc + Number(b.haber), 0),
    [balance],
  );

  const asientoDebe = useMemo(
    () => form.lineas.reduce((acc, l) => acc + Number(l.debe || 0), 0),
    [form],
  );

  const asientoHaber = useMemo(
    () => form.lineas.reduce((acc, l) => acc + Number(l.haber || 0), 0),
    [form],
  );

  const asientoCuadra = asientoDebe === asientoHaber && asientoDebe > 0;

  const createAsiento = async (estado = "draft") => {
    try {
      setSaving(true);

      const payload = {
        ...form,
        estado,
        lineas: form.lineas.map((l) => ({
          ...l,
          debe: Number(l.debe || 0),
          haber: Number(l.haber || 0),
        })),
      };

      if (editingAsiento) {
        await api.put(`/contabilidad/asientos/${editingAsiento.id}`, payload);

        toast.success("Asiento actualizado");
      } else {
        await api.post("/contabilidad/asientos", payload);

        toast.success("Asiento creado");
      }

      setCreateOpen(false);
      setEditingAsiento(null);

      setForm({
        fecha: new Date().toISOString().slice(0, 10),
        cuenta_id: "",
        lineas: [
          {
            concepto: "",
            debe: "",
            haber: "",
          },
        ],
      });
      await load();
    } catch (err) {
      toast.error(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : err?.response?.data?.detail?.[0]?.msg ||
              "No se pudo crear el asiento",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeAsiento = async (id) => {
    try {
      await api.delete(`/contabilidad/asientos/${id}`);

      toast.success("Asiento eliminado");

      await load();
    } catch (err) {
      toast.error(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : err?.response?.data?.detail?.[0]?.msg || "No se pudo eliminar",
      );
    }
  };

  const saveCuenta = async () => {
    try {
      if (editingCuenta) {
        await api.put(`/contabilidad/cuentas/${editingCuenta.id}`, cuentaForm);

        toast.success("Cuenta actualizada");
      } else {
        await api.post("/contabilidad/cuentas", cuentaForm);

        toast.success("Cuenta creada");
      }

      setCuentaModal(false);

      await load();
    } catch (err) {
      toast.error(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : err?.response?.data?.detail?.[0]?.msg || "No se pudo guardar",
      );
    }
  };

  const deleteCuenta = async (id) => {
    try {
      await api.delete(`/contabilidad/cuentas/${id}`);

      toast.success("Cuenta eliminada");

      await load();
    } catch (err) {
      toast.error(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : err?.response?.data?.detail?.[0]?.msg || "No se pudo eliminar",
      );
    }
  };

  return (
    <div className="p-6 md:p-10">
      {/* HEADER */}
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
          Gestión
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-med-text">
              Contabilidad
            </h1>

            <p className="text-med-muted mt-2">
              Gestiona asientos, balances y partidas contables.
            </p>
          </div>

          <button
            onClick={() => {
              setCreateOpen(true);
              setEditingAsiento(null);
              setForm({
                fecha: new Date().toISOString().slice(0, 10),
                cuenta_id: "",
                lineas: [
                  {
                    concepto: "",
                    debe: "",
                    haber: "",
                  },
                ],
              });
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-med-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <Plus size={18} />
            Nuevo asiento
          </button>
        </div>
      </header>

      {/* TABS */}
      <div className="flex flex-wrap gap-2 mb-8">
        {[
          {
            key: "asientos",
            label: "Asientos",
            icon: Receipt,
          },
          {
            key: "balance",
            label: "Balance",
            icon: Calculator,
          },
          {
            key: "cuentas",
            label: "Cuentas",
            icon: Bank,
          },
        ].map((t) => {
          const Icon = t.icon;

          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "bg-med-primary text-white"
                  : "bg-med-surface border border-med-border text-med-text hover:border-med-primary"
              }`}
            >
              <Icon size={18} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* LOADING */}
      {loading && (
        <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
          Cargando contabilidad...
        </div>
      )}

      {/* ASIENTOS */}
      {!loading && tab === "asientos" && (
        <div className="space-y-5">
          {asientos.length === 0 ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              No hay asientos contables.
            </div>
          ) : (
            asientos.map((a) => (
              <motion.div
                key={a.id}
                layout
                className="rounded-2xl border border-med-border bg-med-surface p-5"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-med-muted">
                      {a.fecha}
                    </div>

                    <h3 className="mt-1 text-lg font-bold text-med-text">
                      {a.cuenta_codigo} · {a.cuenta_nombre}
                    </h3>
                  </div>

                  <div className="flex flex-col items-end gap-3">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        a.estado === "closed"
                          ? "bg-green-100 text-green-700"
                          : "bg-orange-100 text-orange-700"
                      }`}
                    >
                      {a.estado === "closed" ? "Cerrado" : "Borrador"}
                    </span>

                    {a.estado === "draft" && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingAsiento(a);

                            setForm({
                              fecha: a.fecha,
                              cuenta_id: a.cuenta_id,
                              lineas: a.lineas.map((l) => ({
                                concepto: l.concepto,
                                debe: l.debe,
                                haber: l.haber,
                              })),
                            });

                            setCreateOpen(true);
                          }}
                          className="flex h-10 w-10 items-center justify-center rounded-xl border border-med-border text-med-text hover:bg-med-bg"
                        >
                          <PencilSimple size={18} />
                        </button>

                        <button
                          onClick={() => removeAsiento(a.id)}
                          className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 text-red-500 hover:bg-red-50"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 space-y-2">
                  {a.lineas.map((l, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between rounded-xl bg-med-bg px-4 py-3"
                    >
                      <div>
                        <div className="font-medium text-med-text">
                          {l.concepto}
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm">
                        <div className="min-w-[90px] text-right text-green-600 font-semibold">
                          {Number(l.debe).toFixed(2)} €
                        </div>

                        <div className="min-w-[90px] text-right text-red-500 font-semibold">
                          {Number(l.haber).toFixed(2)} €
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* BALANCE */}
      {!loading && tab === "balance" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-med-border bg-med-surface p-5">
              <div className="text-sm text-med-muted">Total debe</div>

              <div className="mt-2 text-3xl font-bold text-green-600">
                {totalDebe.toFixed(2)}€
              </div>
            </div>

            <div className="rounded-2xl border border-med-border bg-med-surface p-5">
              <div className="text-sm text-med-muted">Total haber</div>

              <div className="mt-2 text-3xl font-bold text-red-500">
                {totalHaber.toFixed(2)}€
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-med-border bg-med-surface">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-med-bg">
                  <tr className="text-left">
                    <th className="px-5 py-4 text-sm font-semibold text-med-text">
                      Cuenta
                    </th>

                    <th className="px-5 py-4 text-right text-sm font-semibold text-med-text">
                      Debe
                    </th>

                    <th className="px-5 py-4 text-right text-sm font-semibold text-med-text">
                      Haber
                    </th>

                    <th className="px-5 py-4 text-right text-sm font-semibold text-med-text">
                      Saldo
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {balance.map((b) => (
                    <tr
                      key={b.cuenta_id}
                      className="border-t border-med-border"
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium text-med-text">
                          {b.codigo} · {b.nombre}
                        </div>

                        <div className="text-xs uppercase tracking-wide text-med-muted mt-1">
                          {b.tipo}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-right font-semibold text-green-600">
                        {Number(b.debe).toFixed(2)}€
                      </td>

                      <td className="px-5 py-4 text-right font-semibold text-red-500">
                        {Number(b.haber).toFixed(2)}€
                      </td>

                      <td className="px-5 py-4 text-right font-bold text-med-text">
                        {Number(b.saldo).toFixed(2)}€
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CUENTAS */}
      {!loading && tab === "cuentas" && (
        <div>
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => {
                setEditingCuenta(null);

                setCuentaForm({
                  codigo: "",
                  nombre: "",
                  tipo: "gasto",
                });

                setCuentaModal(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-med-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Plus size={18} />
              Nueva cuenta
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {cuentas.map((c) => (
              <motion.div
                key={c.id}
                layout
                className="rounded-2xl border border-med-border bg-med-surface p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${tipoColor(
                        c.tipo,
                      )}`}
                    >
                      {c.tipo}
                    </span>

                    <div className="mt-5 flex items-center gap-2">
                      <span className="mt-1 text-lg text-med-muted">
                        {c.codigo} ·
                      </span>
                      <h3 className="mt-1 text-lg font-bold text-med-text">
                        {c.nombre}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCuenta(c);

                      setCuentaForm({
                        codigo: c.codigo,
                        nombre: c.nombre,
                        tipo: c.tipo,
                      });

                      setCuentaModal(true);
                    }}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-med-border text-med-text hover:bg-med-bg"
                  >
                    <PencilSimple size={18} />
                  </button>

                  <button
                    onClick={() => deleteCuenta(c.id)}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 text-red-500 hover:bg-red-50"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            className="w-full max-w-4xl rounded-3xl border border-med-border bg-white p-8 shadow-2xl"
          >
            {/* HEADER */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingAsiento ? "Editar asiento" : "Nuevo asiento"}
                </h2>

                <p className="mt-2 text-gray-600">
                  Registra un movimiento contable.
                </p>
              </div>

              <button
                onClick={() => setCreateOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50"
              >
                <X size={18} />
              </button>
            </div>

            {/* FORM */}
            <div className="mt-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Fecha
                  </label>

                  <input
                    type="date"
                    value={form.fecha}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        fecha: e.target.value,
                      })
                    }
                    className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Cuenta
                  </label>

                  <select
                    value={form.cuenta_id}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        cuenta_id: e.target.value,
                      })
                    }
                    className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                  >
                    <option value="">Selecciona cuenta</option>

                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.codigo} · {c.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* LÍNEAS */}
              <div className="space-y-3">
                {form.lineas.map((l, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_auto] gap-3 rounded-2xl border border-gray-200 p-4"
                  >
                    <input
                      value={l.concepto}
                      onChange={(e) =>
                        updateLinea(idx, "concepto", e.target.value)
                      }
                      placeholder="Ej: Pago pólvora"
                      className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                    />

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Debe"
                      value={l.debe}
                      onChange={(e) => updateLinea(idx, "debe", e.target.value)}
                      className="rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                    />

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Haber"
                      value={l.haber}
                      onChange={(e) =>
                        updateLinea(idx, "haber", e.target.value)
                      }
                      className="rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                    />

                    <button
                      onClick={() => removeLinea(idx)}
                      className="flex h-12 w-12 items-center justify-center rounded-xl border border-red-200 text-red-500 hover:bg-red-50"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                ))}
              </div>

              {/* ACTIONS */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <button
                  onClick={addLinea}
                  className="rounded-xl border border-gray-200 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-50"
                >
                  + Añadir línea
                </button>

                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    Debe:{" "}
                    <span className="font-semibold text-green-600">
                      {asientoDebe.toFixed(2)}€
                    </span>
                  </div>

                  <div className="text-sm text-gray-500">
                    Haber:{" "}
                    <span className="font-semibold text-red-500">
                      {asientoHaber.toFixed(2)}€
                    </span>
                  </div>

                  <div
                    className={`mt-2 text-sm font-semibold ${
                      asientoCuadra ? "text-green-600" : "text-red-500"
                    }`}
                  >
                    {asientoCuadra
                      ? "✓ El asiento cuadra"
                      : "✗ El asiento no cuadra"}
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCreateOpen(false)}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>

                <div className="flex flex-wrap gap-3">
                  <button
                    disabled={saving}
                    onClick={() => createAsiento("draft")}
                    className="rounded-xl border border-orange-200 bg-orange-50 px-5 py-2.5 font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-50"
                  >
                    Guardar borrador
                  </button>

                  <button
                    disabled={saving || !asientoCuadra}
                    onClick={() => createAsiento("closed")}
                    className="rounded-xl bg-med-primary px-5 py-2.5 font-semibold text-white disabled:opacity-50"
                  >
                    Cerrar asiento
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {cuentaModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            className="w-full max-w-lg rounded-3xl border border-med-border bg-white p-8 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingCuenta ? "Editar cuenta" : "Nueva cuenta"}
                </h2>

                <p className="mt-2 text-gray-600">
                  Configura una partida contable.
                </p>
              </div>

              <button
                onClick={() => setCuentaModal(false)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-8 space-y-5">
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Código
                </label>

                <input
                  value={cuentaForm.codigo}
                  onChange={(e) =>
                    setCuentaForm({
                      ...cuentaForm,

                      codigo: e.target.value,
                    })
                  }
                  placeholder="Ej: 572"
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Nombre
                </label>

                <input
                  value={cuentaForm.nombre}
                  onChange={(e) =>
                    setCuentaForm({
                      ...cuentaForm,

                      nombre: e.target.value,
                    })
                  }
                  placeholder="Ej: Banco"
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Tipo
                </label>

                <select
                  value={cuentaForm.tipo}
                  onChange={(e) =>
                    setCuentaForm({
                      ...cuentaForm,

                      tipo: e.target.value,
                    })
                  }
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-med-primary"
                >
                  <option value="activo">Activo</option>

                  <option value="pasivo">Pasivo</option>

                  <option value="ingreso">Ingreso</option>

                  <option value="gasto">Gasto</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCuentaModal(false)}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>

                <button
                  onClick={saveCuenta}
                  className="rounded-xl bg-med-primary px-5 py-2.5 font-semibold text-white"
                >
                  {editingCuenta ? "Guardar cambios" : "Crear cuenta"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
